// src/services/cuitService.js
/**
 * CUIT Service - Handles CUIT validation and client lookup
 * 
 * NOTE: Uses axios (not fetch) for automatic token refresh via AuthProvider interceptors
 */
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

/**
 * Search for a CUIT in the local backend database
 * @param {string} cuit - CUIT number to search (with or without dashes)
 * @returns {Promise<Object|null>} Client data if found, null if not found
 */
export const searchCuitLocal = async (cuit) => {
    // Clean CUIT (remove dashes if present)
    const cleanCuit = cuit.replace(/-/g, '');
    
    try {
        console.log('[cuitService] Searching CUIT locally:', cleanCuit);

        const response = await axios.get(
            `${NODE_BACKEND_API_URL}/clients/search-cuit/${cleanCuit}`,
            { validateStatus: (status) => status === 200 || status === 404 }
        );

        if (response.status === 404) {
            // Client not found in local database
            console.log('[cuitService] Client not found in local database');
            return null;
        }

        console.log('[cuitService] Client found in local database:', response.data);
        
        return {
            source: 'local',
            ...response.data
        };

    } catch (error) {
        console.error('[cuitService] Error searching CUIT locally:', error);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(errorMessage);
    }
};

/**
 * Validate CUIT with TusFacturas API (AFIP data)
 * @param {string} cuit - CUIT number to validate
 * @returns {Promise<Object>} AFIP client data
 */
export const validateCuitAFIP = async (cuit) => {
    // Clean CUIT (remove dashes if present)
    const cleanCuit = cuit.replace(/-/g, '');
    
    try {
        console.log('[cuitService] Validating CUIT with AFIP via TusFacturas:', cleanCuit);

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/clients/validate-cuit-afip`,
            { cuit: cleanCuit }
        );

        const data = response.data;
        console.log('[cuitService] AFIP validation successful:', data);
        
        return {
            source: 'afip',
            documento_nro: cleanCuit,
            documento_tipo: 'CUIT',
            razon_social: data.razon_social,
            condicion_impositiva: data.condicion_impositiva,
            direccion: data.direccion,
            localidad: data.localidad,
            provincia: data.provincia,
            codigopostal: data.codigopostal,
            estado: data.estado,
            actividad: data.actividad || []
        };

    } catch (error) {
        console.error('[cuitService] Error validating CUIT with AFIP:', error);
        
        // Handle specific AFIP errors
        if (error.response?.data?.afipError) {
            throw new Error(`Error AFIP: ${error.response.data.message}`);
        }
        
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(errorMessage);
    }
};

/**
 * Search CUIT with local fallback to AFIP
 * First tries local database, then falls back to TusFacturas API if not found
 * @param {string} cuit - CUIT number to search
 * @returns {Promise<Object>} Client data with source indicator
 */
export const searchCuitWithFallback = async (cuit) => {
    try {
        console.log('[cuitService] Starting CUIT search with fallback:', cuit);
        
        // First, try local database
        const localResult = await searchCuitLocal(cuit);
        
        if (localResult) {
            console.log('[cuitService] Found in local database');
            return localResult;
        }

        // If not found locally, try AFIP via TusFacturas
        console.log('[cuitService] Not found locally, trying AFIP...');
        const afipResult = await validateCuitAFIP(cuit);
        
        return afipResult;

    } catch (error) {
        console.error('[cuitService] Error in CUIT search with fallback:', error);
        throw error;
    }
};

/**
 * Save client data to local database
 * @param {Object} clientData - Client data to save
 * @returns {Promise<Object>} Saved client data with ID
 */
export const saveClientToDatabase = async (clientData) => {
    try {
        console.log('[cuitService] Saving client to database:', clientData);

        const response = await axios.post(
            `${NODE_BACKEND_API_URL}/clients/save`,
            clientData
        );

        console.log('[cuitService] Client saved successfully:', response.data);
        return response.data;

    } catch (error) {
        console.error('[cuitService] Error saving client:', error);
        const errorMessage = error.response?.data?.message || error.message;
        throw new Error(errorMessage);
    }
};
